import { useEffect, useRef, useState } from "react";

// Pick the first container/codec the current browser can actually record.
// iOS Safari only supports audio/mp4 (AAC); Chrome/Firefox prefer webm/opus.
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/aac",
];

function pickMimeType() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

export function extensionForMime(mime = "") {
  if (mime.includes("mp4") || mime.includes("aac") || mime.includes("m4a")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("webm")) return "webm";
  return "webm";
}

function getDurationFromElement(url) {
  return new Promise((resolve, reject) => {
    const el = document.createElement("audio");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      const d = el.duration;
      el.src = "";
      resolve(Number.isFinite(d) ? d : 0);
    };
    el.onerror = () => reject(new Error("metadata load failed"));
    el.src = url;
  });
}

export default function useRecorder() {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioMimeType, setAudioMimeType] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch (e) {}
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      if (audioURL) URL.revokeObjectURL(audioURL);

      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== "closed") {
            audioContextRef.current.close();
          }
        } catch (e) {}
      }
    };
  }, [audioURL]);

  async function startRecording() {
    if (isRecording) return;
    setError(null);

    if (typeof MediaRecorder === "undefined" ||
        !navigator.mediaDevices?.getUserMedia) {
      setError("Audio recording is not supported on this browser. Please update your browser or use a different device.");
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setError("Microphone access was blocked. Please allow mic permission and try again.");
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    const mimeType = pickMimeType();
    let recorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch (err) {
      setError("This device cannot record audio in a supported format.");
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data?.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      // Use the codec the browser actually produced, not a hard-coded one.
      const actualType = recorder.mimeType || mimeType || "audio/mp4";
      const blob = new Blob(chunksRef.current, { type: actualType });
      setAudioBlob(blob);
      setAudioMimeType(actualType);

      if (audioURL) URL.revokeObjectURL(audioURL);

      const url = URL.createObjectURL(blob);
      setAudioURL(url);

      // Compute duration. decodeAudioData can't handle every container
      // (e.g. webm on Safari), so fall back to a hidden <audio> element.
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
        setDurationSeconds(Math.round(decoded.duration));
      } catch (err) {
        try {
          const d = await getDurationFromElement(url);
          setDurationSeconds(Math.round(d) || 0);
        } catch (e) {
          setDurationSeconds(0);
        }
      }
    };

    recorder.start(1000);
    setIsRecording(true);
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
  }

  function reset() {
    setAudioBlob(null);
    setAudioURL(null);
    setAudioMimeType("");
    setDurationSeconds(0);
    setError(null);
    chunksRef.current = [];
  }

  return {
    startRecording,
    stopRecording,
    reset,
    isRecording,
    audioBlob,
    audioURL,
    audioMimeType,
    durationSeconds,
    error,
  };
}